<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="3" />

    <div class="mb-3">
      <div class="row">
        <div class="col-11"><h4>Autenticación de dos factores</h4></div>
        <div class="col-1">
          <button class="btn-help" type="button" title="Abrir ayuda"
            @click="showModalHelp = true" aria-label="Abrir ayuda de Autenticación de dos factores">
            <span class="material-symbols-outlined">help</span>
          </button>
        </div>
      </div>
    </div>

    <Modal v-model:modelValue="showModalHelp" title="Ayuda — Personas">
      <AyudaConfiguracionMFA />
    </Modal>

    <hr>

    <!-- Tarjeta MFA -->
    <div class="fact-card mb-4">

      <!-- Header -->
      <div class="fact-header">
        <div class="d-flex align-items-center gap-2">
          <span class="material-symbols-outlined" style="font-size:20px;color:#003d99">
            security
          </span>
          <span class="fact-numero">Autenticación de dos factores (MFA)</span>
          <span class="fact-badge" :class="mfaHabilitado ? 'badge-emitida' : 'badge-anulada'">
            {{ mfaHabilitado ? 'Activo' : 'Inactivo' }}
          </span>
        </div>
      </div>

      <!-- Body -->
      <div class="fact-body">

        <!-- Estado: MFA desactivado -->
        <template v-if="!mfaHabilitado && paso === 0">
          <div class="mfa-info-box mb-4">
            <span class="material-symbols-outlined mfa-info-icon">shield_lock</span>
            <div>
              <p class="mfa-info-title">Protege tu cuenta con una segunda capa de seguridad</p>
              <p class="mfa-info-desc">
                Al activar MFA, necesitarás un código de 6 dígitos de tu aplicación
                autenticadora cada vez que inicies sesión.
              </p>
            </div>
          </div>
          <div class="mfa-apps mb-4">
            <p class="fact-seccion-titulo">
              <span class="material-symbols-outlined" style="font-size:15px;vertical-align:-2px">
                smartphone
              </span>
              Aplicaciones compatibles
            </p>
            <div class="mfa-apps-list">
              <span class="mfa-app-chip">Google Authenticator</span>
              <span class="mfa-app-chip">Microsoft Authenticator</span>
              <span class="mfa-app-chip">Authy</span>
            </div>
          </div>
          <button
            class="btn btn-sm btn-primary d-flex align-items-center gap-1"
            :disabled="cargando"
            @click="iniciarConfiguracion"
          >
            <span class="material-symbols-outlined" style="font-size:16px">add_moderator</span>
            {{ cargando ? 'Generando...' : 'Activar MFA' }}
          </button>
        </template>

        <!-- Paso 1: Escanear QR -->
        <template v-if="paso === 1">
          <div class="fact-row mb-2">
            <div class="fact-field fact-field--full">
              <p class="fact-seccion-titulo">
                <span class="material-symbols-outlined" style="font-size:15px;vertical-align:-2px">
                  qr_code_2
                </span>
                Paso 1 — Escanea el código QR
              </p>
              <p class="mfa-step-desc">
                Abre tu aplicación autenticadora y escanea este código QR.
                Si no puedes escanearlo, ingresa la clave manualmente.
              </p>
              <div class="mfa-qr-wrapper">
                <img :src="qrUri" alt="Código QR MFA" class="mfa-qr-img" />
              </div>
            </div>
          </div>

          <!-- Clave manual -->
          <div class="fact-row mb-3">
            <div class="fact-field">
              <p class="fact-seccion-titulo">
                <span class="material-symbols-outlined" style="font-size:15px;vertical-align:-2px">
                  key
                </span>
                Clave de configuración manual
              </p>
              <div class="mfa-secret-box">
                <span class="mfa-secret-text">{{ secretoVisible ? secreto : '••••••••••••••••' }}</span>
                <div class="d-flex gap-1">
                  <button class="mfa-secret-btn" @click="secretoVisible = !secretoVisible"
                    :title="secretoVisible ? 'Ocultar' : 'Mostrar'">
                    <span class="material-symbols-outlined" style="font-size:16px">
                      {{ secretoVisible ? 'visibility_off' : 'visibility' }}
                    </span>
                  </button>
                  <button class="mfa-secret-btn" @click="copiarSecreto" title="Copiar">
                    <span class="material-symbols-outlined" style="font-size:16px">content_copy</span>
                  </button>
                </div>
              </div>
              <span v-if="copiado" class="mfa-copiado">¡Copiado!</span>
            </div>
          </div>

          <div class="d-flex gap-2">
            <button class="btn btn-sm btn-outline-secondary" @click="cancelar">
              Cancelar
            </button>
            <button class="btn btn-sm btn-primary d-flex align-items-center gap-1"
              @click="paso = 2">
              <span class="material-symbols-outlined" style="font-size:16px">arrow_forward</span>
              Continuar
            </button>
          </div>
        </template>

        <!-- Paso 2: Confirmar código OTP -->
        <template v-if="paso === 2">
          <div class="fact-row mb-2">
            <div class="fact-field fact-field--full">
              <p class="fact-seccion-titulo">
                <span class="material-symbols-outlined" style="font-size:15px;vertical-align:-2px">
                  pin
                </span>
                Paso 2 — Confirma con tu aplicación
              </p>
              <p class="mfa-step-desc">
                Ingresa el código de 6 dígitos que muestra tu aplicación
                autenticadora para confirmar la activación.
              </p>
            </div>
          </div>

          <div class="fact-row">
            <div class="fact-field fact-field--sm">
              <span class="fact-lbl">Código de verificación</span>
              <input
                type="text"
                class="form-control form-control-sm otp-confirm-input"
                :class="{ 'is-invalid': errorOtp }"
                v-model.trim="otpCodigo"
                placeholder="000000"
                maxlength="6"
                inputmode="numeric"
                autocomplete="one-time-code"
                @keyup.enter="confirmarActivacion"
              />
              <div v-if="errorOtp" class="invalid-feedback d-block">{{ errorOtp }}</div>
            </div>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
              @click="paso = 1">
              <span class="material-symbols-outlined" style="font-size:16px">arrow_back</span>
              Volver
            </button>
            <button
              class="btn btn-sm btn-success d-flex align-items-center gap-1"
              :disabled="cargando"
              @click="confirmarActivacion"
            >
              <span class="material-symbols-outlined" style="font-size:16px">verified</span>
              {{ cargando ? 'Verificando...' : 'Activar MFA' }}
            </button>
          </div>
        </template>

        <!-- Estado: MFA activado -->
        <template v-if="mfaHabilitado && paso === 0">
          <div class="mfa-active-box mb-4">
            <span class="material-symbols-outlined mfa-active-icon">verified_user</span>
            <div>
              <p class="mfa-info-title">MFA activado correctamente</p>
              <p class="mfa-info-desc">
                Tu cuenta está protegida. Necesitarás tu aplicación autenticadora
                cada vez que inicies sesión.
              </p>
            </div>
          </div>

          <hr class="fact-divider">

          <p class="fact-seccion-titulo text-danger">
            <span class="material-symbols-outlined" style="font-size:15px;vertical-align:-2px">
              warning
            </span>
            Zona de riesgo
          </p>
          <p class="mfa-step-desc mb-2">
            Si desactivas MFA, tu cuenta quedará protegida solo por contraseña.
          </p>
          <button
            class="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
            :disabled="cargando"
            @click="desactivarMfa"
          >
            <span class="material-symbols-outlined" style="font-size:16px">remove_moderator</span>
            {{ cargando ? 'Desactivando...' : 'Desactivar MFA' }}
          </button>
          <button
            class="btn-link-mfa mt-2"
            type="button"
            @click="showModalAuth = true"
          >
            <span class="material-symbols-outlined" style="font-size:15px">smartphone</span>
            ¿Cómo instalar un autenticador?
          </button>
        </template>

      </div>
    </div>

    <Modal v-model:modelValue="showModalAuth" title="Ayuda — Autenticador">
      <AyudaAutenticadorMFA />
    </Modal>

    <Footer />
  </div>
</template>

<script>
import Layout                from '@/components/Layout.vue'
import Footer                from '@/components/Footer.vue'
import AyudaConfiguracionMFA from '@/views/ayuda/AyudaConfiguracionMFA.vue'
import AyudaAutenticadorMFA  from '@/views/ayuda/AyudaAutenticadorMFA.vue'
import api                   from '@/services/api'
import swal                  from 'sweetalert2'

export default {
  name: 'MfaConfig',
  components: { Layout, Footer, AyudaConfiguracionMFA, AyudaAutenticadorMFA },

  data() {
    return {
      mfaHabilitado:  false,
      paso:           0,       // 0=estado, 1=QR, 2=confirmar
      qrUri:          '',
      secreto:        '',
      secretoVisible: false,
      copiado:        false,
      otpCodigo:      '',
      errorOtp:       '',
      cargando:       false,
      showModalHelp:  false,
      showModalAuth:  false,
    }
  },

  async mounted() {
    this.$refs.layoutRef.toggleProcesos()
    this.$refs.layoutRef.toggleReportes()
    this.$refs.layoutRef.toggleParametros()
    this.$refs.layoutRef.toggleAyuda()
    await this.cargarEstadoMfa()
  },

  methods: {

    // Carga estado actual
    async cargarEstadoMfa() {
      try {
        const idUsuario = window.sessionStorage.getItem('idUsuario')
        const { data }  = await api.get(`/vwusuarios/${idUsuario}`)
        this.mfaHabilitado = data.mfaHabilitado
      } catch (e) {
        console.error('Error cargando estado MFA:', e)
      }
    },

    // Paso 1: Generar QR
    async iniciarConfiguracion() {
      this.cargando = true
      console.log("token en session:", window.sessionStorage.getItem('token'))
      try {
        const { data } = await api.post('/auth/mfa/setup')
        this.secreto   = data.secreto
        this.qrUri     = data.qrUri
        this.paso      = 1
      } catch (e) {
        swal.fire({
          icon:              'error',
          title:             'Error',
          text:              'No se pudo generar el código QR.',
          confirmButtonText: 'Cerrar',
          confirmButtonColor:'#dc3545',
        })
      } finally {
        this.cargando = false
      }
    },

    // Paso 2: Confirmar OTP
    async confirmarActivacion() {
      this.errorOtp = ''
      if (!this.otpCodigo) {
        this.errorOtp = 'Ingrese el código.'
        return
      }
      if (!/^\d{6}$/.test(this.otpCodigo)) {
        this.errorOtp = 'El código debe tener 6 dígitos.'
        return
      }
      this.cargando = true
  try {
    await api.post('/auth/mfa/confirm', { codigo: this.otpCodigo })
    this.mfaHabilitado = true
    this.paso          = 0
    this.otpCodigo     = ''
    await swal.fire({
      icon:              'success',
      title:             '¡MFA Activado!',
      text:              'Tu cuenta ahora está protegida con autenticación de dos factores.',
      confirmButtonText: 'Aceptar',
      confirmButtonColor:'#198754',
    })
  } catch (e) {
    if (e.response?.status === 400) {
      // muestra el mensaje del backend
      this.errorOtp = e.response?.data?.message || 'Código incorrecto. Intenta de nuevo.'
    } else {
      swal.fire({
        icon:              'error',
        title:             'Error',
        text:              'No se pudo activar MFA.',
        confirmButtonText: 'Cerrar',
        confirmButtonColor:'#dc3545',
      })
    }
  } finally {
    this.cargando  = false
    this.otpCodigo = ''
  }
},

    // Desactivar MFA
    async desactivarMfa() {
      const confirm = await swal.fire({
        title:             '¿Desactivar MFA?',
        text:              'Tu cuenta quedará protegida solo por contraseña.',
        icon:              'warning',
        showCancelButton:  true,
        confirmButtonText: 'Sí, desactivar',
        cancelButtonText:  'Cancelar',
        confirmButtonColor:'#dc3545',
        cancelButtonColor: '#6c757d',
      })
      if (!confirm.value) return
      this.cargando = true
      try {
        await api.post('/auth/mfa/disable')
        this.mfaHabilitado = false
        await swal.fire({
          icon:              'success',
          title:             'MFA desactivado',
          text:              'La autenticación de dos factores ha sido desactivada.',
          confirmButtonText: 'Aceptar',
          confirmButtonColor:'#198754',
        })
      } catch (e) {
        swal.fire({
          icon:              'error',
          title:             'Error',
          text:              'No se pudo desactivar MFA.',
          confirmButtonText: 'Cerrar',
          confirmButtonColor:'#dc3545',
        })
      } finally {
        this.cargando = false
      }
    },

    // Utilidades
    cancelar() {
      this.paso           = 0
      this.qrUri          = ''
      this.secreto        = ''
      this.secretoVisible = false
      this.otpCodigo      = ''
      this.errorOtp       = ''
    },

    async copiarSecreto() {
      try {
        await navigator.clipboard.writeText(this.secreto)
        this.copiado = true
        setTimeout(() => { this.copiado = false }, 2000)
      } catch {
        console.error('No se pudo copiar')
      }
    },
  },
}
</script>

<style scoped>
/* Reutiliza estilos de fact-card */
.fact-card {
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.fact-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: #f7f8fa;
  border-bottom: 1px solid #e8e8e8;
}
.fact-numero { font-size: 0.9rem; font-weight: 600; color: #444; }
.fact-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}
.badge-emitida { background: #d1fae5; color: #065f46; }
.badge-anulada { background: #fee2e2; color: #991b1b; }
.fact-body  { padding: 16px; }
.fact-row   { display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 12px; }
.fact-field { display: flex; flex-direction: column; flex: 1; min-width: 140px; }
.fact-field--sm   { flex: 0 1 220px; }
.fact-field--full { flex: 1 1 100%; }
.fact-lbl {
  font-size: 0.72rem; font-weight: 600; color: #9aa0ac;
  text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 3px;
}
.fact-seccion-titulo {
  font-size: 0.75rem; font-weight: 600; color: #6c757d;
  text-transform: uppercase; letter-spacing: 0.05em;
  margin: 0 0 8px; display: flex; align-items: center; gap: 5px;
}
.fact-divider { border: none; border-top: 1px solid #ebebeb; margin: 12px 0; }

/* MFA específico */
.mfa-info-box {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: #f0f4ff;
  border: 1px solid #c7d7f9;
  border-radius: 10px;
  padding: 16px;
}
.mfa-info-icon { font-size: 2.5rem; color: #003d99; flex-shrink: 0; }
.mfa-info-title { font-size: 0.9rem; font-weight: 600; color: #003d99; margin: 0 0 4px; }
.mfa-info-desc  { font-size: 0.85rem; color: #555; margin: 0; line-height: 1.5; }

.mfa-active-box {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 16px;
}
.mfa-active-icon { font-size: 2.5rem; color: #15803d; flex-shrink: 0; }

.mfa-apps-list { display: flex; flex-wrap: wrap; gap: 8px; }
.mfa-app-chip {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 0.8rem;
  color: #475569;
}

.mfa-qr-wrapper {
  display: flex;
  justify-content: center;
  padding: 16px;
  background: #f7f8fa;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  margin-bottom: 8px;
}
.mfa-qr-img { width: 180px; height: 180px; }

.mfa-secret-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f7f8fa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 8px 12px;
  gap: 8px;
}
.mfa-secret-text {
  font-family: monospace;
  font-size: 0.95rem;
  letter-spacing: 0.1em;
  color: #333;
  flex: 1;
}
.mfa-secret-btn {
  background: none;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 4px 6px;
  cursor: pointer;
  color: #6c757d;
  display: flex;
  align-items: center;
  transition: all 0.15s;
}
.mfa-secret-btn:hover { background: #e9ecef; color: #003d99; }
.mfa-copiado { font-size: 0.78rem; color: #15803d; margin-top: 4px; }

.mfa-step-desc { font-size: 0.85rem; color: #555; line-height: 1.6; margin-bottom: 12px; }

.otp-confirm-input {
  font-size: 1.3rem;
  letter-spacing: 0.4rem;
  text-align: center;
  font-weight: 600;
}
.btn-link-mfa {
  background: none;
  border: none;
  padding: 0;
  color: #003d99;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  text-decoration: underline;
  text-underline-offset: 2px;
  opacity: 0.85;
  transition: opacity 0.15s;
}
.btn-link-mfa:hover {
  opacity: 1;
  color: #002a6e;
}
</style>
<style src="@/assets/css/listaRegistros.css" scoped></style>